'use client'

import {useCallback, useEffect, useRef, useState} from 'react'
import {
    CheckCircle2,
    Download,
    LogOut,
    Monitor,
    Plus,
    QrCode,
    RefreshCw,
    Search,
    Server,
    Smartphone,
    Trash2,
    User,
    X,
} from 'lucide-react'
import type {LucideIcon} from 'lucide-react'
import type {Account, AccountProfile, Contact} from '@/types'
import {avatarDisplaySrc, avatarFor} from '@/lib/utils'
import {apiDelete, apiGet, apiPost, apiPut} from '@/lib/api'
import {PageHeader} from '@/components/PageHeader'
import {ConfirmDialog} from '@/components/ConfirmDialog'
import {message} from '@/components/Toast'
import {resolveProxyUrl, type ProxyMode} from '@/lib/proxy'

const protocolOptions = [
    {value: 'ipad', label: 'iPad', description: '推荐使用', icon: Smartphone},
    {value: 'mac', label: 'Mac', description: '可过验证', icon: Monitor},
    {value: 'windows', label: 'Windows', description: '可过验证', icon: Monitor},
]

const proxyModeOptions: Array<{value: ProxyMode; label: string; description: string; icon: LucideIcon}> = [
    {value: 'portnest', label: 'PortNest', description: '填写 PortNest ID 自动生成代理', icon: Server},
    {value: 'custom', label: '自定义代理', description: '直接填写 socks5/http 代理 URL', icon: QrCode},
]

type LoginStep = 'form' | 'qr'
const QR_COUNTDOWN_SECONDS = 3 * 60

function formatQrCountdown(totalSeconds: number): string {
    const seconds = Math.max(0, Math.floor(totalSeconds))
    const minutesPart = Math.floor(seconds / 60).toString().padStart(2, '0')
    const secondsPart = (seconds % 60).toString().padStart(2, '0')
    return `${minutesPart}:${secondsPart}`
}

function DownloadLink({label, url}: {label: string; url: string}) {
    if (!url) {
        return (
            <button
                type="button"
                disabled
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-hair bg-gray-50 px-3 text-xs text-muted opacity-60"
                title="未配置下载地址"
            >
                <Download size={14}/>
                {label}
            </button>
        )
    }
    return (
        <a
            href={url}
            download={url.split('/').pop()?.split('?')[0] || undefined}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-accent bg-green-50 px-3 text-xs font-medium text-accent hover:bg-green-100"
        >
            <Download size={14}/>
            {label}
        </a>
    )
}

type ApiAccount = {
    wxid: string
    alias: string
    nickName: string
    avatar: string
    protocol: string
    proxyUrl: string
    status: string
}

type LoginStatusInfo = {
    loginState: number
    loginErrMsg: string
    onlineTime: string
    loginTime: string
    onlineDays: number
    expiryTime: string
}

type Instance = {
    id: string
    name: string
    wxid: string
    alias: string
    avatar: string
    protocol: string
    proxyUrl: string
    status: 'online' | 'offline' | 'expired' | 'logged_out'
    loginInfo?: LoginStatusInfo
}

export function PersonalView({
                                 account,
                                 logout,
                                 qrImage,
                                 loginStatus,
                                 loginLoading,
                                 loginSuccessVersion,
                                 onRefreshQr,
                             }: {
    account: Account | null
    profile: AccountProfile | null
    contacts: Contact[]
    logout: () => void
    refreshProfile: () => void
    profileRefreshing: boolean
    qrImage: string
    loginStatus: string
    loginLoading: boolean
    loginSuccessVersion: number
    onRefreshQr: (input?: { protocol?: string; proxyUrl?: string; wxid?: string }) => Promise<void> | void
}) {
    const [instances, setInstances] = useState<Instance[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 5
    const [total, setTotal] = useState(0)
    const [totalPages, setTotalPages] = useState(1)

    const fetchAccounts = useCallback(async () => {
        setLoading(true)
        try {
            const query = new URLSearchParams({
                page: String(page),
                pageSize: String(PAGE_SIZE),
            })
            if (search.trim()) {
                query.set('search', search.trim())
            }
            const res = await apiGet<{
                success: boolean
                accounts: ApiAccount[]
                total: number
                page: number
                totalPages: number
            }>(`/accounts?${query.toString()}`, {silent: true})
            if (res.success && Array.isArray(res.accounts)) {
                const nextTotalPages = Math.max(1, res.totalPages || 1)
                if (page > nextTotalPages) {
                    setPage(nextTotalPages)
                    return
                }
                setInstances(res.accounts.map((a) => {
                    return {
                        id: a.wxid,
                        name: a.nickName || a.wxid,
                        wxid: a.wxid,
                        alias: a.alias || '',
                        avatar: a.avatar || avatarFor(a.wxid),
                        protocol: a.protocol || '',
                        proxyUrl: a.proxyUrl || '',
                        status: (a.status || 'offline') as Instance['status'],
                    }
                }))
                setTotal(res.total || 0)
                setTotalPages(nextTotalPages)
            }
        } catch {
            // silent
        } finally {
            setLoading(false)
        }
    }, [page, search])

    useEffect(() => {
        fetchAccounts()
    }, [fetchAccounts])

    // 登录/退出后刷新列表
    useEffect(() => {
        fetchAccounts()
    }, [account, fetchAccounts])

    const instanceWxidsKey = instances.map((item) => item.wxid).filter(Boolean).sort().join(',')
    useEffect(() => {
        const wxids = instanceWxidsKey ? instanceWxidsKey.split(',') : []
        if (!wxids.length) return
        const sources = wxids.map((wxid) => {
            const source = new EventSource('/api/accounts/events?wxid=' + encodeURIComponent(wxid))
            const onStatus = (event: MessageEvent) => {
                let payload: {type?: string; wxid?: string; status?: Instance['status']; loginInfo?: LoginStatusInfo}
                try {
                    payload = JSON.parse(event.data)
                } catch {
                    return
                }
                if (payload.type !== 'account_status' || payload.wxid !== wxid || !payload.status) return
                setInstances((prev) => prev.map((acc) => (
                    acc.wxid === wxid ? {...acc, status: payload.status!, loginInfo: payload.loginInfo} : acc
                )))
            }
            source.addEventListener('account_status', onStatus as EventListener)
            return {source, onStatus}
        })
        return () => {
            for (const {source, onStatus} of sources) {
                source.removeEventListener('account_status', onStatus as EventListener)
                source.close()
            }
        }
    }, [instanceWxidsKey])

    // 搜索时重置到第一页，分页由后端完成。
    useEffect(() => {
        setPage(1)
    }, [search])

    const [showLoginPanel, setShowLoginPanel] = useState(false)
    const [loginStep, setLoginStep] = useState<LoginStep>('form')
    const [qrRemainingSeconds, setQrRemainingSeconds] = useState(QR_COUNTDOWN_SECONDS)
    const [protocol, setProtocol] = useState('ipad')
    const [proxyMode, setProxyMode] = useState<ProxyMode>('custom')
    const [portNestId, setPortNestId] = useState('')
    const [proxyUrl, setProxyUrl] = useState('')
    const [proxyTesting, setProxyTesting] = useState(false)
    const [portNestConfig, setPortNestConfig] = useState({proxyAddr: '', windowsUrl: '', macUrl: ''})
    const [warnWithoutProxy, setWarnWithoutProxy] = useState(false)
    const [loginTarget, setLoginTarget] = useState<Instance | null>(null)

    // 仅最终登录成功后关闭弹窗；Mac/Win 的首次登录由服务端续接 iPad 二维码。
    const handledLoginSuccess = useRef(loginSuccessVersion)
    useEffect(() => {
        if (loginSuccessVersion === 0 || handledLoginSuccess.current === loginSuccessVersion) return
        handledLoginSuccess.current = loginSuccessVersion
        message.success('登录成功')
        setShowLoginPanel(false)
        setLoginStep('form')
        void fetchAccounts()
    }, [loginSuccessVersion, fetchAccounts])

    type ConfirmState = {
        open: boolean
        title: string
        description: string
        confirmLabel: string
        danger: boolean
        onConfirm: () => void
    }
    const [confirm, setConfirm] = useState<ConfirmState>({open: false, title: '', description: '', confirmLabel: '确定', danger: false, onConfirm: () => {}})

    // Inline proxy editing
    const [editingProxyWxid, setEditingProxyWxid] = useState<string | null>(null)
    const [editingProxyValue, setEditingProxyValue] = useState('')
    const [savingProxy, setSavingProxy] = useState(false)

    useEffect(() => {
        if (!qrImage) {
            setQrRemainingSeconds(QR_COUNTDOWN_SECONDS)
            return
        }

        const expiresAt = Date.now() + QR_COUNTDOWN_SECONDS * 1000
        const updateCountdown = () => {
            setQrRemainingSeconds(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)))
        }

        updateCountdown()
        const timer = window.setInterval(updateCountdown, 1000)
        return () => window.clearInterval(timer)
    }, [qrImage])

    useEffect(() => {
        apiGet<{success: boolean; proxyAddr: string; windowsUrl: string; macUrl: string; warnWithoutProxy?: boolean}>('/portnest/downloads', {silent: true})
            .then((res) => {
                if (res.success) {
                    setPortNestConfig({
                        proxyAddr: res.proxyAddr || '',
                        windowsUrl: res.windowsUrl || '',
                        macUrl: res.macUrl || '',
                    })
                    setWarnWithoutProxy(res.warnWithoutProxy === true)
                }
            })
            .catch(() => {})
    }, [])

    async function handleSaveProxy(inst: Instance) {
        setSavingProxy(true)
        try {
            const res = await apiPut<{ success: boolean; proxyUrl: string }>(
                `/accounts/${encodeURIComponent(inst.wxid)}/proxy`,
                {proxyUrl: editingProxyValue},
            )
            if (res.success) {
                setInstances((prev) => prev.map((a) =>
                    a.wxid === inst.wxid ? {...a, proxyUrl: res.proxyUrl} : a,
                ))
            }
            setEditingProxyWxid(null)
        } catch {
            // apiPut already toasts
        } finally {
            setSavingProxy(false)
        }
    }

    async function requestLoginQr(resolvedProxyUrl: string) {
        setLoginStep('qr')
        await onRefreshQr({
            protocol,
            proxyUrl: resolvedProxyUrl,
            wxid: loginTarget?.wxid || '',
        })
    }

    async function handleFetchQr() {
        const resolvedProxyUrl = proxyMode === 'portnest'
            ? resolveProxyUrl({proxyMode, portNestId, portNestProxyAddr: portNestConfig.proxyAddr})
            : proxyUrl
        if (warnWithoutProxy && !resolvedProxyUrl.trim()) {
            setConfirm({
                open: true,
                title: '未设置代理',
                description: '当前微信实例没有设置代理，继续登录可能存在网络或账号安全风险。确定要继续吗？',
                confirmLabel: '继续登录',
                danger: false,
                onConfirm: () => {
                    setConfirm((state) => ({...state, open: false}))
                    void requestLoginQr(resolvedProxyUrl)
                },
            })
            return
        }
        await requestLoginQr(resolvedProxyUrl)
    }

    async function handleTestLoginProxy() {
        const resolvedProxyUrl = proxyMode === 'portnest'
            ? resolveProxyUrl({proxyMode, portNestId, portNestProxyAddr: portNestConfig.proxyAddr})
            : proxyUrl.trim()
        if (!resolvedProxyUrl) {
            message.warning('请先填写代理信息')
            return
        }
        setProxyTesting(true)
        try {
            const res = await apiPost<{success: boolean; message?: string; exitIp?: string; province?: string; city?: string}>('/proxy/test', {
                proxyUrl: resolvedProxyUrl,
            })
            if (res.success) {
                message.success(res.message || `代理连通正常${res.exitIp ? `，出口 IP：${res.exitIp}` : ''}`)
            }
        } finally {
            setProxyTesting(false)
        }
    }

    function handleAddInstance() {
        setLoginTarget(null)
        setProtocol('ipad')
        setProxyMode('custom')
        setPortNestId('')
        setProxyUrl('')
        setShowLoginPanel(true)
        setLoginStep('form')
    }

    function handleOpenLogin(inst: Instance) {
        setLoginTarget(inst)
        setProtocol('ipad')
        setProxyMode('custom')
        setPortNestId('')
        setProxyUrl(inst.proxyUrl || '')
        setShowLoginPanel(true)
        setLoginStep('form')
    }

    async function doLogout(inst: Instance) {
        try {
            await apiPost('/login/logout', {wxid: inst.wxid})
            if (account?.wxid === inst.wxid) {
                logout()
            }
            await fetchAccounts()
        } catch {
            // apiPost already toasts the error
        }
    }

    function handleLogout(inst: Instance) {
        setConfirm({
            open: true,
            title: `退出账号「${inst.name}」`,
            description: `确定要退出 ${inst.wxid} 吗？退出后将停止该账号的所有自动化任务。`,
            confirmLabel: '退出',
            danger: false,
            onConfirm: () => {
                setConfirm((s) => ({...s, open: false}))
                doLogout(inst)
            },
        })
    }

    async function doDelete(inst: Instance) {
        try {
            await apiDelete(`/accounts/${encodeURIComponent(inst.wxid)}`)
            if (instances.length <= 1 && page > 1) {
                setPage((current) => Math.max(1, current - 1))
            } else {
                await fetchAccounts()
            }
        } catch {
            // apiDelete already toasts the error
        }
    }

    function handleDelete(inst: Instance) {
        setConfirm({
            open: true,
            title: `删除账号「${inst.name}」`,
            description: `此操作将删除该账号在本设备上的所有数据，包括：\n• 聊天记录与消息\n• 通讯录联系人\n• 标签、自动回复规则\n• 定时任务、朋友圈等\n\n删除后不可恢复，请确认已退出登录。`,
            confirmLabel: '确认删除',
            danger: true,
            onConfirm: () => {
                setConfirm((s) => ({...s, open: false}))
                doDelete(inst)
            },
        })
    }

    function handleCloseLogin() {
        setShowLoginPanel(false)
        setLoginStep('form')
    }

    const panelAvatar = loginTarget ? loginTarget.avatar : null
    const panelName = loginTarget ? loginTarget.name : '新的微信实例'
    const panelSub = loginTarget ? loginTarget.wxid : '扫码成功后自动同步昵称和头像'

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="微信实例"
                description="管理并监控当前系统中所有接入的多开微信账号"
                icon={<Smartphone size={18}/>}
                action={
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" size={14}/>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="搜索昵称或微信号"
                                className="w-52 h-9 bg-white border border-hair rounded pl-8 pr-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                            />
                        </div>
                        <button
                            onClick={handleAddInstance}
                            className="btn-primary flex items-center gap-1.5"
                        >
                            <Plus size={16}/> 添加实例
                        </button>
                    </div>
                }
            />

            <div className="flex-1 overflow-y-auto p-6">
                <div className="w-full max-w-7xl mr-auto">
                    <div className="bg-white rounded-card shadow-card border border-hair overflow-hidden">
                        <div className="overflow-x-auto">
                        <table className="w-full min-w-[1120px] table-fixed text-left">
                            <colgroup>
                                <col className="w-[20%]"/>
                                <col className="w-[16%]"/>
                                <col className="w-[10%]"/>
                                <col className="w-[30%]"/>
                                <col className="w-[15%]"/>
                                <col className="w-[15%]"/>
                            </colgroup>
                            <thead className="bg-gray-50 border-b border-hair text-[#374151] text-sm">
                            <tr>
                                <th className="px-6 py-4 font-medium whitespace-nowrap">账号信息</th>
                                <th className="px-6 py-4 font-medium whitespace-nowrap">微信账号</th>
                                <th className="px-6 py-4 font-medium whitespace-nowrap">协议</th>
                                <th className="px-6 py-4 font-medium whitespace-nowrap">代理地址</th>
                                <th className="px-6 py-4 font-medium whitespace-nowrap">状态</th>
                                <th className="px-6 py-4 font-medium text-right whitespace-nowrap">操作</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                            {instances.map((acc) => (
                                <tr key={acc.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <img
                                                src={avatarDisplaySrc(acc.avatar, acc.wxid)}
                                                alt="avatar"
                                                className={`w-10 h-10 rounded-md border border-hair mr-3 ${acc.status !== 'online' ? 'grayscale opacity-60' : ''}`}
                                            />
                                            <div>
                                                <div className="font-medium text-[#374151]">{acc.name}</div>
                                                <div className="text-xs text-[#374151] font-mono mt-0.5">{acc.wxid}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="block truncate text-sm text-soft" title={acc.alias || '-'}>
                                            {acc.alias || '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm">
                                        <span className="bg-blue-50 text-blue-600 border border-blue-100 px-2 py-1 rounded text-xs font-mono">
                                            {acc.protocol || '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm relative">
                                        {/* 占位内容，保持列宽稳定 */}
                                        <span className={`block truncate text-xs text-soft font-normal ${editingProxyWxid === acc.wxid ? 'invisible' : ''}`}>
                                            {acc.proxyUrl || '点击设置代理'}
                                        </span>
                                        {editingProxyWxid === acc.wxid ? (
                                            <div className="absolute inset-y-1 left-3 right-3 flex items-center gap-1 z-10">
                                                <input
                                                    type="text"
                                                    value={editingProxyValue}
                                                    onChange={(e) => setEditingProxyValue(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSaveProxy(acc)
                                                        if (e.key === 'Escape') setEditingProxyWxid(null)
                                                    }}
                                                    className="input text-xs py-1 px-2 flex-1 min-w-0"
                                                    placeholder="socks5://127.0.0.1:7890"
                                                    autoFocus
                                                    disabled={savingProxy}
                                                />
                                                <button
                                                    onClick={() => handleSaveProxy(acc)}
                                                    disabled={savingProxy}
                                                    className="text-xs text-accent hover:text-green-700 px-1.5 py-1 rounded hover:bg-green-50 shrink-0"
                                                    title="保存"
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    onClick={() => setEditingProxyWxid(null)}
                                                    className="text-xs text-muted hover:text-ink px-1.5 py-1 rounded hover:bg-gray-100 shrink-0"
                                                    title="取消"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <span
                                                onClick={() => {
                                                    setEditingProxyWxid(acc.wxid)
                                                    setEditingProxyValue(acc.proxyUrl)
                                                }}
                                                className="absolute inset-0 flex items-center px-6 cursor-pointer text-soft hover:text-ink text-xs font-normal"
                                                title="点击编辑代理地址"
                                            >
                                                <span className="block w-full truncate">
                                                    {acc.proxyUrl || '点击设置代理'}
                                                </span>
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        {acc.status === 'online' ? (
                                            <span className="inline-flex items-center whitespace-nowrap text-xs font-medium text-accent bg-green-50 px-2.5 py-1 rounded-full border border-green-200" title={acc.loginInfo?.onlineTime || ''}>
                                                <span className="w-1.5 h-1.5 rounded-full bg-accent mr-1.5"></span> 在线
                                            </span>
                                        ) : acc.status === 'expired' ? (
                                            <span className="inline-flex items-center whitespace-nowrap text-xs font-medium text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full border border-orange-200" title={acc.loginInfo?.loginErrMsg || ''}>
                                                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-1.5"></span> 会话过期
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center whitespace-nowrap text-xs font-medium text-muted bg-gray-100 px-2.5 py-1 rounded-full border border-gray-200">
                                                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-1.5"></span> 离线
                                            </span>
                                        )}
                                        {acc.loginInfo?.onlineTime && acc.status === 'online' && (
                                            <div className="text-[11px] text-muted mt-1">{acc.loginInfo.onlineTime}</div>
                                        )}
                                        {acc.loginInfo?.loginErrMsg && acc.status === 'expired' && (
                                            <div className="text-[11px] text-orange-500 mt-1">{acc.loginInfo.loginErrMsg}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            {acc.status === 'online' ? (
                                                <button
                                                    onClick={() => handleLogout(acc)}
                                                    className="text-xs text-red-500 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded border border-transparent hover:border-red-100 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                                                >
                                                    <LogOut size={14}/> 退出
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => handleOpenLogin(acc)}
                                                        className={`text-xs px-4 py-1.5 inline-flex items-center gap-1.5 whitespace-nowrap ${acc.status === 'expired' ? 'btn-warning' : 'btn-primary'}`}
                                                    >
                                                        <QrCode size={14}/> {acc.status === 'expired' ? '重新登入' : '登入'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(acc)}
                                                        className="text-xs text-gray-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded border border-transparent hover:border-red-100 transition-colors"
                                                        title="删除账号及所有数据"
                                                    >
                                                        <Trash2 size={14}/>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {instances.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-muted">
                                        {search ? '没有匹配的微信实例。' : '暂无任何微信实例，请点击右上角添加。'}
                                    </td>
                                </tr>
                            )}
                            {loading && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-8 h-8 border-[3px] border-accent border-t-transparent rounded-full animate-spin"/>
                                            <span className="text-sm text-muted">正在加载实例列表…</span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                        </div>
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-3 border-t border-hair bg-gray-50/50 text-sm">
                                <span className="text-muted">
                                    {search ? `匹配 ${total}` : `共 ${total}`} 个实例，第 {page}/{totalPages} 页
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        className="px-3 py-1 rounded border border-hair bg-white text-ink hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        上一页
                                    </button>
                                    <button
                                        type="button"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        className="px-3 py-1 rounded border border-hair bg-white text-ink hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        下一页
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 登录弹窗 */}
            {showLoginPanel && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-card shadow-xl border border-hair flex flex-col overflow-hidden w-full max-w-[520px] max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-hair flex items-center justify-between bg-gray-50/70">
                            <span className="font-medium text-ink flex items-center gap-2">
                                <QrCode size={18} className="text-accent"/>
                                {loginTarget ? '重新登入' : '登入微信实例'}
                            </span>
                            <button
                                type="button"
                                onClick={handleCloseLogin}
                                className="text-muted hover:text-ink p-1 rounded hover:bg-gray-200 transition-colors"
                            >
                                <X size={18}/>
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto">
                            <div className="flex items-center p-3 bg-gray-50 border border-hair rounded-lg">
                                {panelAvatar ? (
                                    <img src={panelAvatar} alt="avatar"
                                         className="w-12 h-12 rounded-md border border-hair mr-3"/>
                                ) : (
                                    <div className="w-12 h-12 rounded-md mr-3 bg-gray-100 border border-hair flex items-center justify-center text-muted">
                                        <User size={26}/>
                                    </div>
                                )}
                                <div>
                                    <div className="font-medium text-ink">{panelName}</div>
                                    <div className="text-xs text-muted mt-0.5">{panelSub}</div>
                                </div>
                            </div>

                            {loginStep === 'form' ? (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-soft">登录方式</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {protocolOptions.map((opt) => {
                                                const Icon = opt.icon
                                                const active = protocol === opt.value
                                                return (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => setProtocol(opt.value)}
                                                        className={`flex min-w-0 flex-col items-center gap-2 rounded-card border bg-white px-2 py-3 text-center transition-all ${
                                                            active
                                                                ? 'border-accent bg-green-50/40 ring-1 ring-accent'
                                                                : 'border-hair hover:border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-card bg-gray-100 text-slate-500">
                                                            <Icon size={18}/>
                                                        </span>
                                                        <span className="min-w-0">
                                                            <span className="block whitespace-nowrap text-sm font-bold leading-5 text-ink">{opt.label}</span>
                                                            <span className="mt-0.5 block text-xs leading-4 text-slate-500">{opt.description}</span>
                                                        </span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                        <p className="text-xs leading-5 text-muted">
                                            优先iPad登录，需要验证时，可任Mac或Win；首次登录成功后将自动退出，重新扫码登录
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-soft">代理方式</label>
                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            {proxyModeOptions.map((opt) => {
                                                const Icon = opt.icon
                                                const active = proxyMode === opt.value
                                                return (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => setProxyMode(opt.value)}
                                                        className={`flex min-h-[72px] items-center gap-3 rounded-card border bg-white p-3 text-left transition-all ${
                                                            active
                                                                ? 'border-accent bg-green-50/40 ring-1 ring-accent'
                                                                : 'border-hair hover:border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-card bg-gray-100 text-slate-500">
                                                            <Icon size={18}/>
                                                        </span>
                                                        <span className="min-w-0">
                                                            <span className="block text-base font-bold leading-5 text-ink">{opt.label}</span>
                                                            <span className="mt-0.5 block text-xs leading-4 text-slate-500">{opt.description}</span>
                                                        </span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {proxyMode === 'portnest' ? (
                                            <>
                                                <label className="text-sm text-soft">
                                                    PortNest ID <span className="text-muted text-xs">选填</span>
                                                </label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={portNestId}
                                                        onChange={(e) => setPortNestId(e.target.value)}
                                                        placeholder="请输入 PortNest ID"
                                                        className="input"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn-secondary h-9 shrink-0"
                                                        disabled={proxyTesting}
                                                        onClick={() => void handleTestLoginProxy()}
                                                    >
                                                        {proxyTesting ? '测试中...' : '测试'}
                                                    </button>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    <DownloadLink label="Windows 客户端" url={portNestConfig.windowsUrl}/>
                                                    <DownloadLink label="Mac 客户端" url={portNestConfig.macUrl}/>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <label className="text-sm text-soft">
                                                    代理 URL <span className="text-muted text-xs">选填</span>
                                                </label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={proxyUrl}
                                                        onChange={(e) => setProxyUrl(e.target.value)}
                                                        placeholder="socks5://user:pass@127.0.0.1:7890"
                                                        className="input"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn-secondary h-9 shrink-0"
                                                        disabled={proxyTesting}
                                                        onClick={() => void handleTestLoginProxy()}
                                                    >
                                                        {proxyTesting ? '测试中...' : '测试'}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="pt-4">
                                        <button
                                            type="button"
                                            onClick={handleFetchQr}
                                            disabled={loginLoading}
                                            className="btn-primary w-full flex items-center justify-center gap-2"
                                        >
                                            {loginLoading
                                                ? <RefreshCw size={16} className="animate-spin"/>
                                                : <QrCode size={16}/>
                                            }
                                            获取登录二维码
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center">
                                    {loginLoading ? (
                                        <div className="py-12 flex flex-col items-center">
                                            <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin mb-4"/>
                                            <span className="text-sm text-muted">正在向服务器请求安全二维码...</span>
                                        </div>
                                    ) : qrImage ? (
                                        <div className="py-6 flex flex-col items-center">
                                            <div className="relative border border-hair p-2 rounded-xl shadow-sm mb-4 bg-white">
                                                <img src={qrImage} alt="登录二维码" className="w-40 h-40 object-contain"/>
                                            </div>
                                            <span className="text-[15px] font-medium text-ink mb-1">请使用微信扫一扫</span>
                                            <span className="text-xs text-muted">
                                                二维码剩余有效时间：{formatQrCountdown(qrRemainingSeconds)}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="py-12 flex flex-col items-center">
                                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-muted">
                                                <CheckCircle2 size={30}/>
                                            </div>
                                            <span className="text-[15px] font-medium text-ink">二维码已结束</span>
                                        </div>
                                    )}
                                    {loginStatus && (
                                        <p role="status" aria-live="polite" className="text-sm text-muted text-center leading-relaxed min-h-5">{loginStatus}</p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleFetchQr}
                                        disabled={loginLoading}
                                        className="btn-primary mt-6 w-full flex items-center justify-center gap-2"
                                    >
                                        <RefreshCw size={16} className={loginLoading ? 'animate-spin' : ''}/>
                                        重新获取二维码
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirm.open}
                title={confirm.title}
                description={confirm.description}
                confirmLabel={confirm.confirmLabel}
                danger={confirm.danger}
                onConfirm={confirm.onConfirm}
                onCancel={() => setConfirm((s) => ({...s, open: false}))}
            />
        </section>
    )
}
